/** @jsxImportSource @emotion/react */
import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useAtomValue } from 'jotai'
import { DatabaseIcon } from 'lucide-react'
import * as React from 'react'
import {
  getDefaultColumnVisibility,
  groupByHazardName,
  tabAtomMap,
} from '../utils/data.submodule.utils'
import { columns } from './DataLayerTable.columns'
import * as styles from './DataLayerTable.styles'
import { ExportDataTable } from './DataLayerTableExport'
import { HazardGroupRow } from './HazardGroupRow'

type TabAtomKey = keyof typeof tabAtomMap

export function DataLayerTable({ activeTab }: { activeTab: TabAtomKey }) {
  const rows = useAtomValue(tabAtomMap[activeTab])
  const [sorting, setSorting] = React.useState<SortingState>(
    activeTab === 'hazards' ? [{ id: 'name', desc: false }] : [{ id: 'category', desc: false }],
  )
  const [pageIndex, setPageIndex] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(20)
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() =>
    getDefaultColumnVisibility(activeTab),
  )
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set())

  const [columnsMenuAnchor, setColumnsMenuAnchor] = React.useState<null | HTMLElement>(null)
  const isColumnsMenuOpen = Boolean(columnsMenuAnchor)

  const handleOpenColumnsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setColumnsMenuAnchor(event.currentTarget)
  }

  const handleCloseColumnsMenu = () => {
    setColumnsMenuAnchor(null)
  }

  const handleToggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  // Reset filters + pagination + columns + expanded state when switching tabs
  React.useEffect(() => {
    setGlobalFilter('')
    setColumnFilters([])
    setPageIndex(0)
    setColumnVisibility(getDefaultColumnVisibility(activeTab))
    setExpandedGroups(new Set())
  }, [activeTab])

  // Reset pagination when data changes
  React.useEffect(() => {
    setPageIndex(0)
  }, [rows])

  const table = useReactTable({
    data: rows || [],
    columns,
    state: {
      sorting,
      pagination: { pageIndex, pageSize },
      globalFilter,
      columnFilters,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater
      setPageIndex(next.pageIndex)
      setPageSize(next.pageSize)
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: 'includesString',
    enableSortingRemoval: false,
  })

  const isHazardsTab = activeTab === 'hazards'

  // grouping by hazard_name for the hazards tab
  const groupedRows = React.useMemo(() => {
    if (!isHazardsTab) return null
    const filteredRows = table.getPrePaginationRowModel().rows.map((r) => r.original)
    return groupByHazardName(filteredRows)
  }, [isHazardsTab, table])

  const visibleColCount = table.getVisibleLeafColumns().length

  const exportColumns = table
    .getVisibleLeafColumns()
    .filter((column) => column.id !== 'actions')
    .map((column) => ({
      id: column.id,
      header: typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id,
    }))

  const exportRows = table.getPrePaginationRowModel().rows.map((row) => {
    const rowValues = row.getAllCells().reduce<Record<string, unknown>>((acc, cell) => {
      acc[cell.column.id] = cell.getValue()
      return acc
    }, {})

    return exportColumns.reduce<Record<string, unknown>>((acc, column) => {
      acc[column.id] = rowValues[column.id]
      return acc
    }, {})
  })

  return (
    <Paper css={styles.tableWrapper}>
      {/* ── Toolbar ── */}
      <Box css={styles.toolbar}>
        <Box css={styles.toolbarLeft}>
          <TextField
            size="small"
            placeholder="Search elements"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            css={styles.searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" css={styles.categoryFilter}>
            <TextField
              select
              size="small"
              value={(table.getColumn('category')?.getFilterValue() as string) ?? ''}
              onChange={(e) => {
                const value = e.target.value
                table.getColumn('category')?.setFilterValue(value === '' ? undefined : value)
              }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>Filter by category</em>
              </MenuItem>
              {Array.from(new Set(rows.map((r) => r.category)))
                .sort((a, b) => a.localeCompare(b))
                .map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
            </TextField>
          </FormControl>
        </Box>

        <Box>
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={handleOpenColumnsMenu}
          >
            Columns
          </Button>

          <Menu
            anchorEl={columnsMenuAnchor}
            open={isColumnsMenuOpen}
            onClose={handleCloseColumnsMenu}
            MenuListProps={{ dense: true }}
          >
            {table
              .getAllLeafColumns()
              .filter((c) => c.getCanHide() && c.id !== 'actions')
              .map((column) => (
                <MenuItem
                  key={column.id}
                  onClick={() => column.toggleVisibility(!column.getIsVisible())}
                >
                  <Checkbox size="small" checked={column.getIsVisible()} />
                  <ListItemText
                    primary={
                      typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id
                    }
                  />
                </MenuItem>
              ))}
          </Menu>
        </Box>
      </Box>

      {/* ── Table ── */}
      <TableContainer css={styles.tableContainer}>
        <Table stickyHeader aria-label="data table" sx={{ minWidth: 900 }}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  const active = sortDir !== false
                  const direction: 'asc' | 'desc' = sortDir === 'desc' ? 'desc' : 'asc'

                  return (
                    <TableCell
                      key={header.id}
                      align={header.column.id === 'actions' ? 'right' : 'left'}
                      css={styles.headerCell}
                      sortDirection={active ? direction : false}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <TableSortLabel
                          active={active}
                          direction={direction}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableSortLabel>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {/* ── Hazards tab: Grouped view ── */}
            {isHazardsTab && groupedRows ? (
              groupedRows.size === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} css={styles.emptyRow}>
                    <EmptyData title="No results found" icon={<DatabaseIcon />} />
                  </TableCell>
                </TableRow>
              ) : (
                Array.from(groupedRows.entries()).map(([hazardName, groupRows]) => {
                  const isExpanded = expandedGroups.has(hazardName)
                  const scenarioIds = new Set(groupRows.map((r) => r.id))
                  const tanstackRows = table
                    .getPrePaginationRowModel()
                    .rows.filter((r) => scenarioIds.has(r.original.id))

                  return (
                    <React.Fragment key={hazardName}>
                      <HazardGroupRow
                        label={hazardName}
                        count={groupRows.length}
                        colSpan={visibleColCount}
                        isExpanded={isExpanded}
                        onToggle={() => handleToggleGroup(hazardName)}
                        owner={groupRows[0]?.owner ?? null}
                        confidenceRating={groupRows[0]?.confidenceRating ?? null}
                        suitability={groupRows[0]?.suitability ?? null}
                      />
                      {isExpanded &&
                        tanstackRows.map((row) => (
                          <TableRow hover tabIndex={-1} key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <TableCell
                                key={cell.id}
                                align={cell.column.id === 'actions' ? 'right' : 'left'}
                                sx={{ '& a': { maxWidth: '100%' }, pl: 5 }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                    </React.Fragment>
                  )
                })
              )
            ) : (
              /* ── all other tabs(Elements and Contextual): flat view ── */
              <>
                {table.getRowModel().rows.map((row) => (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        align={cell.column.id === 'actions' ? 'right' : 'left'}
                        sx={{ '& a': { maxWidth: '100%' } }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {table.getRowModel().rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} css={styles.emptyRow}>
                      <EmptyData title="No results found" icon={<DatabaseIcon />} />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      {!isHazardsTab && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 25, 50]}
          component="div"
          count={table.getFilteredRowModel().rows.length}
          rowsPerPage={pageSize}
          page={pageIndex}
          onPageChange={(_e, newPage) => table.setPageIndex(newPage)}
          onRowsPerPageChange={(e) => table.setPageSize(parseInt(e.target.value, 10))}
        />
      )}

      <ExportDataTable
        columns={exportColumns}
        rows={exportRows}
        fileName={`risk-data-${activeTab}.csv`}
      />
    </Paper>
  )
}
